import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  AiChatContext,
  type AiOperatingMode,
  type AiContextTab,
  type AiInteractionPhase,
} from './aiChatContextCore'
import type { AiTierId } from '../types/ai'
import { DEFAULT_AI_TIER } from '../types/ai'

const TAB_STORAGE_KEY = 'timelining_magic_active_tab'
const PLANNING_STORAGE_KEY = 'timelining_magic_planning_mode'
/**
 * Last-used AI tier is stored per-user (not per-workspace) at this shell layer
 * because the provider mounts above any workspace-scoped context. When the
 * switcher hook resolves a per-workspace tier in Phase C, it reads/writes
 * `${TIER_STORAGE_KEY_PREFIX}${workspaceId}` directly.
 */
const TIER_STORAGE_KEY = 'timelining_magic_last_tier'
export const TIER_STORAGE_KEY_PREFIX = 'timelining_magic_last_tier__'

const isTierId = (raw: unknown): raw is AiTierId =>
  raw === 'line' || raw === 'line_pro' || raw === 'line_max'

const readPersistedTab = (): AiOperatingMode => {
  if (typeof window === 'undefined') return 'CREATE'
  const raw = window.localStorage.getItem(TAB_STORAGE_KEY)
  return raw === 'EXECUTE' ? 'EXECUTE' : 'CREATE'
}

const readPersistedPlanningMode = (): boolean => {
  if (typeof window === 'undefined') return true
  const raw = window.localStorage.getItem(PLANNING_STORAGE_KEY)
  return raw === null ? true : raw === '1'
}

const readPersistedTier = (): AiTierId => {
  if (typeof window === 'undefined') return DEFAULT_AI_TIER
  const raw = window.localStorage.getItem(TIER_STORAGE_KEY)
  return isTierId(raw) ? raw : DEFAULT_AI_TIER
}

export const AiChatProvider = ({ children }: { children: ReactNode }) => {
  const [operatingMode, setOperatingMode] = useState<AiOperatingMode>(readPersistedTab)
  const [isPlanningMode, setIsPlanningMode] = useState<boolean>(readPersistedPlanningMode)
  const [activeContextTab, setActiveContextTab] = useState<AiContextTab>('NODES')
  const [isSidecarOpen, setIsSidecarOpen] = useState(() => (
    typeof window === 'undefined' || !window.matchMedia('(max-width: 880px)').matches
  ))
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [phase, setPhase] = useState<AiInteractionPhase>('IDLE')
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null)
  const [tier, setTierState] = useState<AiTierId | null>(readPersistedTier)

  useEffect(() => {
    window.localStorage.setItem(TAB_STORAGE_KEY, operatingMode)
  }, [operatingMode])

  useEffect(() => {
    window.localStorage.setItem(PLANNING_STORAGE_KEY, isPlanningMode ? '1' : '0')
  }, [isPlanningMode])

  useEffect(() => {
    if (tier) window.localStorage.setItem(TIER_STORAGE_KEY, tier)
  }, [tier])

  const setTier = useCallback((next: AiTierId) => {
    setTierState(next)
  }, [])

  const value = useMemo(() => ({
    operatingMode,
    setOperatingMode,
    isPlanningMode,
    setIsPlanningMode,
    activeContextTab,
    setActiveContextTab,
    isSidecarOpen,
    setIsSidecarOpen,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    phase,
    setPhase,
    activeNodeId,
    setActiveNodeId,
    tier,
    setTier,
  }), [activeContextTab, activeNodeId, isMobileMenuOpen, isPlanningMode, isSidecarOpen, operatingMode, phase, setOperatingMode, setTier, tier])

  return <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>
}
